<script setup lang="ts">
import commonContent from "~/content/common.json";

const authStore = useAuthStore();
const ordersStore = useOrdersStore();
const premiumStore = usePremiumStore();
const uiStore = useUiStore();
const { staticUrl } = useImageUrl();

const route = useRoute();
const router = useRouter();
const mobileMenuOpen = ref(false);
const scrolled = ref(false);
const showAccountMenu = ref(false);
const isMobile = ref(false);

const cartCount = computed(() => ordersStore.cart?.items?.length ?? 0);

const mobileCartTotal = computed(() => {
  const isPremium = ordersStore.cartType === "premium";
  const isGroupOrder = isPremium && !!premiumStore.selectedPremiumOrderConfig?.isGroupOrder;

  if (isPremium && isGroupOrder) {
    const isOwner = authStore.user?.role === "owner";
    if (isOwner) return "£0.00";
    const credit = premiumStore.premiumOrderCredit as Record<string, unknown> | false;
    const empGross = credit && typeof credit === "object" ? Number(credit.employee_gross || 0) : 0;
    return `£${(Math.abs(empGross) / 100).toFixed(2)}`;
  }

  const total = Number(ordersStore.order?.total || ordersStore.cart?.total || 0);
  return `£${(total / 100).toFixed(2)}`;
});

// Keep premiumOrderCredit in sync so mobileCartTotal is reactive
watch(
  [() => ordersStore.cart, () => premiumStore.selectedPremiumOrderConfig],
  ([cart]) => {
    if (ordersStore.cartType === "premium" && cart) {
      premiumStore.calculatePremiumCredit({
        is_group: !!premiumStore.selectedPremiumOrderConfig?.isGroupOrder,
        maker_id: (cart as Record<string, unknown>).maker
          ? ((cart as Record<string, unknown>).maker as Record<string, unknown>).id
          : undefined,
        products:
          cart.items?.map((item: Record<string, unknown>) => ({
            product_id: (item.product as Record<string, unknown>)?.id,
            quantity: item.quantity,
            appliedModifiers: item.appliedModifiers,
          })) || [],
      });
    }
  },
  { deep: true, immediate: true },
);

const isHomePage = computed(() => route.name === "index");

// Pages that paint the body pink need the white logo to remain visible.
// Anything else uses the default pink logo against the warm canvas.
// Mobile is always pink-on-white now (the navbar bar is solid white on
// mobile) so the pink-canvas exception only applies to desktop.
const pinkCanvasRoutes = ["contact"];
const logoSrc = computed(() =>
  staticUrl(
    !isMobile.value && pinkCanvasRoutes.includes(route.name as string)
      ? "/Images/logos/logo-white.png"
      : "/Images/logos/logo-pink.png",
  ),
);

const hiddenCartRoutes = [
  "index",
  "checkout",
  "orders",
  "orders-upcoming",
  "orders-previous",
  "premium-schedule-change-maker",
  "premium-store-slug",
  "premium-menu-slug",
];
// Cart icon only renders once the customer has actually added something —
// an empty basket icon is visual clutter on every page they haven't yet
// shopped on.
const showDesktopCart = computed(
  () => !hiddenCartRoutes.includes(route.name as string) && cartCount.value > 0,
);

// The search input only makes sense on pages where searching for or
// browsing makers is the primary action — i.e. /search, /search/[slug],
// and the (regular) /store/[slug] page. Anywhere else (contact, about,
// checkout, orders, premium, account, etc.) it ends up firing into a
// store/page that may not consume it, or just clutters the nav.
const visibleSearchRoutes = ["search", "search-slug", "store-slug"];
// Reads router.currentRoute, not useRoute(): the layout sits outside <NuxtPage>, and
// Nuxt only re-syncs useRoute()'s route object on a Suspense resolve or a top-level
// component swap. Leaving /store/[slug]/product/[productSlug] back to /store/[slug]
// is neither — the child unmounts under the same parent — so useRoute() here would
// keep reporting the product route's name after the URL (and router.currentRoute)
// have already reverted, hiding the search bar until an unrelated navigation forced
// a resync.
const showDesktopSearch = computed(() =>
  visibleSearchRoutes.includes(router.currentRoute.value.name as string),
);

interface NavItem {
  icon: string;
  name: string;
  value: string;
  link: string;
  show: boolean;
}

const navItems = computed<NavItem[]>(() => {
  if (authStore.isLoggedIn) {
    return [
      { icon: "book", name: "about", value: "About Us", link: "/about", show: isHomePage.value },
      { icon: "receipt", name: "Orders", value: "Orders", link: "/orders", show: true },
    ];
  }
  return [
    { icon: "book", name: "about", value: "About Us", link: "/about", show: isHomePage.value },
    { icon: "hat-chef", name: "join", value: "Become a Maker", link: "/join", show: isHomePage.value },
    { icon: "phone", name: "enquire", value: "Contact Sales", link: "/contact", show: true },
  ];
});

const userDisplayName = computed(() => {
  if (!authStore.user?.first_name) return "Login";
  const name = authStore.user.first_name;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("resize", handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
  if (typeof document !== "undefined") {
    document.body.style.overflow = "";
  }
});

// Lock background scroll while the mobile drawer is open so the page
// behind doesn't scroll under the open menu.
watch(mobileMenuOpen, (open) => {
  if (typeof document === "undefined") return;
  document.body.style.overflow = open ? "hidden" : "";
});

function handleScroll() {
  scrolled.value = window.scrollY > 10;
}

function handleResize() {
  isMobile.value = window.innerWidth <= 768;
}

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  uiStore.setShowServices(false);
}

function toggleCart() {
  if (window.innerWidth < 768) {
    ordersStore.setShowCart(!ordersStore.showCart);
  } else if (ordersStore.cart?.maker?.slug) {
    navigateTo(`/store/${ordersStore.cart.maker.slug}`);
  }
}

function toggleServices() {
  uiStore.showSearch = false;
  uiStore.setShowServices(!uiStore.showServices);
}

function handleLogoClick() {
  if (route.name === "index" || route.name === "search") {
    navigateTo("/");
  } else {
    navigateTo("/search");
  }
}

// Close mobile menu on route change
watch(
  () => route.fullPath,
  () => {
    mobileMenuOpen.value = false;
    showAccountMenu.value = false;
    uiStore.setShowServices(false);
  },
);
</script>

<template>
  <header :class="['navbar', { scrolled }]">
    <div class="navbar__container">
      <!-- Logo -->
      <div
        class="navbar__logo"
        @click="handleLogoClick"
      >
        <img
          :src="logoSrc"
          alt="And Dine"
          loading="lazy"
        />
      </div>

      <!-- Desktop Search Bar (inline, hidden on certain pages) -->
      <SearchBar
        v-if="showDesktopSearch"
        class="navbar__search"
        @search-submit="
          (q: string) => {
            if (q) navigateTo(`/search?q=${q}`);
          }
        "
      />

      <!-- Mobile Hamburger -->
      <div class="navbar__items--icons-mobile">
        <fa
          class="icon"
          :icon="['fal', 'bars']"
          @click="toggleMobileMenu"
        />
      </div>

      <!-- Desktop Nav Items -->
      <div
        class="navbar__items"
        :class="mobileMenuOpen && 'show-nav'"
      >
        <!-- Services dropdown on homepage -->
        <div
          v-if="isHomePage"
          class="navbar__items--tab-wrapper"
          @click="toggleServices"
        >
          <button
            type="button"
            class="link services"
            :class="uiStore.showServices && 'selected-link'"
            :aria-expanded="uiStore.showServices"
            aria-controls="services-menu"
          >
            <fa
              class="icon"
              :icon="['fal', 'utensils']"
            />
            Our Services
            <fa :icon="['fal', `chevron-${uiStore.showServices ? 'up' : 'down'}`]" />
          </button>
        </div>

        <!-- Nav links (client-only to avoid SSR hydration mismatch on auth state) -->
        <ClientOnly>
          <template
            v-for="(item, index) in navItems"
            :key="`tab_${index}`"
          >
            <div
              v-if="item.show"
              class="navbar__items--tab-wrapper"
            >
              <NuxtLink
                class="link"
                :class="route.path.startsWith(item.link) && 'selected-link'"
                :to="item.link"
              >
                <fa
                  class="icon"
                  :icon="['fal', item.icon]"
                />
                {{ item.value }}
              </NuxtLink>
            </div>
          </template>

          <!-- User / Account -->
          <div class="navbar__items--tab-wrapper">
            <NuxtLink
              class="link services"
              :class="
                (route.path.startsWith('/account') ||
                  (!authStore.isLoggedIn && route.path === '/login')) &&
                'selected-link'
              "
              :to="authStore.isLoggedIn ? '/account/details' : '/login'"
            >
              <fa
                class="icon"
                :icon="['fal', 'user']"
              />
              {{ userDisplayName }}
            </NuxtLink>
          </div>

          <template #fallback>
            <!-- SSR fallback: render the logged-out nav directly so the
                 server-rendered markup matches `navItems` (computed)
                 for the unauthenticated case. -->
            <div class="navbar__items--tab-wrapper">
              <NuxtLink
                class="link"
                to="/about"
              >
                <fa
                  class="icon"
                  :icon="['fal', 'book']"
                />
                About Us
              </NuxtLink>
            </div>
            <div class="navbar__items--tab-wrapper">
              <NuxtLink
                class="link"
                to="/join"
              >
                <fa
                  class="icon"
                  :icon="['fal', 'hat-chef']"
                />
                Become a Maker
              </NuxtLink>
            </div>
            <div class="navbar__items--tab-wrapper">
              <NuxtLink
                class="link"
                to="/contact"
              >
                <fa
                  class="icon"
                  :icon="['fal', 'phone']"
                />
                Contact Sales
              </NuxtLink>
            </div>
            <div class="navbar__items--tab-wrapper">
              <NuxtLink
                class="link services"
                to="/login"
              >
                <fa
                  class="icon"
                  :icon="['fal', 'user']"
                />
                Login
              </NuxtLink>
            </div>
          </template>
        </ClientOnly>

        <!-- Desktop Cart Icon -->
        <div
          v-if="showDesktopCart"
          class="navbar__items--icons-desktop"
          @click="toggleCart"
        >
          <fa
            :icon="['fal', 'basket-shopping']"
            class="large-icon"
          />
          <h4
            v-if="cartCount > 0"
            class="small-text"
          >
            {{ cartCount }}
          </h4>
        </div>
      </div>
    </div>
  </header>
  <!-- Mobile Cart Banner — Teleported to body so the navbar's backdrop-filter
       doesn't reanchor its fixed positioning to the header (which would pin
       it at the top instead of the bottom of the viewport).
       Hidden while the basket overlay is open (!showCart) so the cart owns
       the full viewport below the nav on mobile — same guard applied
       consistently on store/[slug].vue, premium/store/[slug].vue, and
       orders/[id]/edit.vue. The mobile cart overlay itself is now also
       Teleported to body (was previously trapped in a z-index:1 stacking
       context by the unscoped cross-page .maker styles, which is why this
       banner used to paint over its Confirm Order button pre-fix). -->
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="ordersStore.cart && cartCount > 0 && route.name !== 'checkout' && !uiStore.showMenus && !ordersStore.showCart"
        class="mobile-cart"
      >
        <div
          class="mobile-cart__panel"
          @click="toggleCart"
        >
          <p class="white-text">{{ cartCount }}</p>
          <p class="white-text">View basket</p>
          <p class="white-text heavy">
            {{ mobileCartTotal }}
          </p>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style lang="scss">
.mobile-cart {
  position: fixed;
  width: 92.5%;
  padding: 1.5rem 4%;
  z-index: $z-nav-banner;
  bottom: 0;
  background-color: var(--color-white-1);
  display: none;
  @media (max-width: $tablet) {
    display: block;
  }
  &__panel {
    background-color: var(--color-pink-1);
    min-height: var(--tap);
    padding: 0 1rem;
    display: flex;
    align-items: center;
    border-radius: var(--radius);
    cursor: pointer;
    justify-content: space-between;
    p {
      font-size: 1rem;
      margin: 0;
    }
    &:hover {
      transform: scale(1.02);
    }
  }
}

.navbar {
  width: 90%;
  padding: 0 5%;
  z-index: $z-navbar;
  height: 5rem;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background-color: transparent;
  align-items: center;
  justify-content: center;
  transition:
    box-shadow var(--dur-base) var(--ease-out),
    background-color var(--dur-base) var(--ease-out),
    backdrop-filter var(--dur-base) var(--ease-out);
  // Scrolled state: frosted translucent surface + soft shadow.
  // Feature-detected so browsers without backdrop-filter (older Firefox)
  // keep a solid white bar instead of a transparent one.
  &.scrolled {
    box-shadow: var(--shadow-2);
    @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
      background-color: rgba(255, 255, 255, 0.72);
      -webkit-backdrop-filter: saturate(180%) blur(14px);
      backdrop-filter: saturate(180%) blur(14px);
    }
  }
  // Mobile: solid white bar at all times — overrides both the idle
  // transparent state AND the scrolled frosted state so the nav stays
  // an opaque white surface against any page background (especially
  // brightly coloured heros like /contact).
  @media (max-width: $tablet) {
    &,
    &.scrolled {
      background-color: var(--color-white-1);
      box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.06);
      -webkit-backdrop-filter: none;
      backdrop-filter: none;
    }
  }
  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  &__logo {
    cursor: pointer;
    width: 6rem;
    display: flex;
    img {
      width: 100%;
    }
    @media (max-width: $tablet) {
      width: 20%;
    }
  }
  &__search {
    flex: 1;
    width: 100%;
    max-width: 24rem;
    margin: 0 2rem;
    @media (max-width: $laptop) {
      display: none;
    }
  }
  .show-nav {
    position: fixed;
    display: flex !important;
  }
  &__items {
    display: flex;
    list-style-type: none;
    position: relative;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
    z-index: 8888;
    // Mobile drawer: full-screen overlay sitting below the slim header.
    // Width is 100vw and box-sizing border-box so its 5% horizontal
    // padding is included — without that, the drawer's children would
    // be 100% of the padded width and push past the viewport edge
    // (which is exactly what made the selected Orders chip spill into
    // the right gutter).
    @media (max-width: $laptop) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      gap: 0.25rem;
      width: 100vw;
      height: calc(100vh - 5rem);
      background-color: var(--color-white-1);
      padding: 1.5rem 5%;
      display: none;
      left: 0;
      top: 5rem;
      box-sizing: border-box;
      overflow-y: auto;
      overflow-x: hidden;
    }
    &--tab-wrapper {
      position: relative;
      cursor: pointer;
      margin: 0;
      box-sizing: border-box;
      @media (max-width: $laptop) {
        text-align: left;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        margin: 0.25rem 0;
        justify-content: center;
        width: 100%;
        a {
          // Border-box so width:100% + padding stays inside the wrapper
          // (this was the root of the Orders-chip overflow into the
          // right gutter).
          box-sizing: border-box;
          width: 100%;
          padding: 0 1rem;
          min-height: 3rem;
          font-size: var(--text-base);
        }
      }
      a {
        // Nav links sized to match the global `button` rhythm: text-sm,
        // tap-height min, and pill-radius so the chrome reads as a single
        // family with the cream search-bar pill next to them.
        color: var(--color-grey-1) !important;
        margin: 0;
        display: flex;
        min-height: var(--tap);
        padding: 0 var(--space-4);
        font-size: var(--text-sm);
        font-weight: var(--weight-medium);
        align-items: center;
        border-radius: var(--radius);
        transition:
          background-color var(--dur-fast) var(--ease-out),
          color var(--dur-fast) var(--ease-out);
      }
      .icon {
        color: var(--color-grey-1);
        margin-right: 0.5rem;
      }
      // Hover wash: muted pink tint with primary text — pill stays the same
      // shape as the idle state so it never reshapes between states.
      &:not(.selected-link):not(:has(.selected-link)):hover {
        background-color: var(--color-pink-4);
        border-radius: var(--radius);
        a,
        button.link,
        .icon {
          color: var(--color-pink-1) !important;
        }
      }
      // Selected → solid primary pink with white text, same pill shape.
      &.selected-link,
      .selected-link,
      &.selected-link:hover,
      .selected-link:hover,
      &:has(.selected-link):hover {
        background-color: var(--color-pink-1);
        color: var(--color-white-1) !important;
        border-radius: var(--radius);
        a,
        button.link,
        .icon {
          color: var(--color-white-1) !important;
        }
      }
      .link {
        text-decoration: none;
        cursor: pointer;
        font-size: var(--text-sm) !important;
        white-space: nowrap;
      }
      // Services is a disclosure control, not a navigation link — render as
      // <button> so crawlers don't see a non-crawlable bare <a>.
      button.link {
        appearance: none;
        background: none;
        border: 0;
        margin: 0;
        font: inherit;
        color: inherit;
        display: inline-flex;
        align-items: center;
        // Match the `a` box above so the hover/selected pill is the same
        // shape as every other nav item (global `button` supplies the
        // min-height and radius; only the padding needs restating).
        padding: 0 var(--space-4);
        // `!important` is load-bearing: global `button` in main.scss sets
        // `font-weight: var(--weight-semibold) !important`, which no amount
        // of specificity beats. GalanoGrotesqueMedium is a single 400 face,
        // so 600 renders as synthetic bold while the `<a>` siblings sit at
        // --weight-medium — that's the visible mismatch, not a 500/600 step.
        font-weight: var(--weight-medium) !important;
      }
      // The reset above sets `background: none` at a higher specificity than
      // `.selected-link`, so the open-state pill never painted on the
      // <button> — white text on a white navbar meant the whole label and
      // icon vanished while the dropdown was open, reappearing only on hover
      // (which paints the wrapper's own pink). Repaint it here, after the
      // reset, so open state reads the same as a selected <a>.
      button.link.selected-link {
        background-color: var(--color-pink-1);
      }
      .services {
        align-items: center;
        // Only the trailing chevron (un-classed <fa>) gets the left
        // margin — the leading `.icon` already has its own
        // margin-right. Without this scope, the user / utensils
        // leading icons inherited a 0.5rem left gap that pushed them
        // off-baseline from the text.
        svg:not(.icon) {
          margin-left: 0.5rem;
        }
      }
    }
    &--icons-desktop {
      cursor: pointer;
      // Wrap the basket icon + count in one rounded pill so the dark icon
      // always sits on a known white backdrop, regardless of what's behind
      // the transparent navbar (e.g. /contact hero is full pink — without
      // this the icon was unreadable on pink).
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      margin-left: 1.25rem;
      border-radius: var(--radius-sm);
      background-color: var(--color-white-1);
      border: 1px solid var(--color-grey-3);
      transition:
        background-color var(--dur-fast) var(--ease-out),
        border-color var(--dur-fast) var(--ease-out);
      h4 {
        margin: 0;
        color: var(--color-grey-1) !important;
        font-family: "GalanoGrotesqueMedium", sans-serif !important;
        font-size: 0.75rem;
        line-height: 1;
      }
      @media (max-width: $tablet) {
        display: none;
      }
      svg {
        color: var(--color-grey-1);
        font-size: 1.25rem !important;
        width: 1.25rem !important;
        transition: color var(--dur-fast) var(--ease-out);
      }
      &:hover {
        border-color: var(--color-pink-1);
        svg {
          color: var(--color-pink-1);
        }
      }
    }
    &--icons-mobile {
      cursor: pointer;
      float: right;
      padding-right: 1rem;
      display: flex;
      align-items: center;
      @media (min-width: $laptop) {
        display: none;
      }
      @media (max-width: $tablet) {
        padding: 0;
      }
      // Hamburger / mobile search icons → foreground ink (no pink on idle).
      svg {
        margin: 5px;
        color: var(--color-grey-1);
      }
    }
  }
  &__submenu-account {
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 3.8rem;
    left: 50%;
    transform: translateX(-50%);
    width: max-content;
    padding: 1rem;
    border-radius: 0 0 0.4rem 0.4rem;
    background-color: var(--color-grey-3);
    color: var(--color-grey-1);
    z-index: 9999;
    @media (max-width: $laptop) {
      position: relative;
      color: var(--color-white-1);
      background-color: var(--color-pink-1);
      top: 0;
      left: 0;
      transform: translateX(0);
      width: calc(100% - 2rem);
    }
    &--tab {
      text-decoration: none;
      padding: 0.5rem 1rem;
      border-radius: 0.4rem;
      text-align: center;
      cursor: pointer;
      color: inherit;
      &:hover {
        background-color: var(--color-white-1);
      }
      @media (max-width: $tablet) {
        width: 95%;
      }
    }
  }
}
</style>
